import { useState, useMemo, useCallback } from 'react';

type SortDirection = 'asc' | 'desc';

interface SortState {
  key: string;
  direction: SortDirection;
}

function getValue(obj: unknown, key: string): unknown {
  return (obj as Record<string, unknown>)[key];
}

function compare(a: unknown, b: unknown, direction: SortDirection): number {
  const multiplier = direction === 'asc' ? 1 : -1;

  if (a == null && b == null) return 0;
  if (a == null) return multiplier;
  if (b == null) return -multiplier;

  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * multiplier;
  }

  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return (Number(a) - Number(b)) * multiplier;
  }

  const aStr = String(a).toLowerCase();
  const bStr = String(b).toLowerCase();
  return aStr.localeCompare(bStr) * multiplier;
}

export function useTableSort<T>(
  items: T[],
  defaultKey: string,
  defaultDirection: SortDirection = 'asc'
) {
  const [sort, setSort] = useState<SortState>({
    key: defaultKey,
    direction: defaultDirection,
  });

  const toggle = useCallback((key: string) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) =>
        compare(getValue(a, sort.key), getValue(b, sort.key), sort.direction)
      ),
    [items, sort.key, sort.direction]
  );

  return { sorted, sortKey: sort.key, sortDirection: sort.direction, toggle };
}
